import * as React from 'react';
import {
    ClickAwayListener,
    createStyles,
    Dialog,
    DialogContent,
    IconButton,
    Link,
    Popper,
    Theme,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import CloseIcon from '@mui/icons-material/Close';
import MuiDialogTitle from '@mui/material/DialogTitle';
import LrStreetView from 'components/LrStreetView';
import { LabeledData } from 'components/Input';
import { formatTruncatedDollar } from 'helpers/formatters';
import { useContext, useEffect, useState } from 'react';
import Location, { ProximityDetails } from 'application/Models/Location';
import { InstallationProviderContext } from '../../../../../application/Providers/Installation';
import { getLocationDetails } from 'services/location';
import ProximityLocation from 'application/Models/ProximityLocation';
import { MapLocation } from 'types/location.type';
import AggregationDetail, { AggDetailsProximityLocation } from './AggregationDetail';
import { DefaultTheme, makeStyles, withStyles, WithStyles, WithTheme, withTheme } from '@mui/styles';

interface LocationDetailDialogProps {
    locationData: MapLocation | Location;
    open: boolean;
    onClose?: () => void;
}

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 0,
            padding: theme.spacing(2),
        },
        closeButton: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
        proximityLocations: {
            cursor: 'pointer',
            '& label': {
                cursor: 'pointer',
            },
        },
    });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

const delineations = [1, 2.5, 5, 10];

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} size="large">
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const LocationDetailDialog: React.FunctionComponent<LocationDetailDialogProps & WithTheme> = (props) => {
    const { locationData, open, onClose, theme } = { ...props };
    const productProvider = useContext(InstallationProviderContext);

    const [locationDetails, setLocationDetails] = useState<Location>({} as Location);
    const [locationProximityDetails, setLocationProximityDetails] = useState<ProximityDetails>();
    const [locationAggPopperAnchorEl, setLocationAggPopperAnchorEl] = useState<HTMLElement | null>(null);
    const [locationAggProxLocations, setLocationAggProxLocations] = useState<AggDetailsProximityLocation[]>([]);
    const [locationAggDistance, setLocationAggDistance] = useState(0);
    const classes = makeStyles(styles(theme))();

    // Get the appropriate values component from the product provider
    const LocationValuesComponent = productProvider.getLocationValuesComponent();

    const fetchLocationDetails = async () => {
        let details = {} as Location;
        if ('locationId' in locationData && !!locationData.locationId) {
            details = await getLocationDetails(
                locationData.locationId,
                true,
                productProvider.getPrimaryProductLineCode()
            );
            setLocationDetails(details);
        } else if ('proximityLocations' in locationData) {
            details = locationData;
        }
        if (details.proximityDetails) {
            setLocationProximityDetails(details.proximityDetails);
        } else if (details.proximityLocations) {
            const proximityDetails = {
                nearestLocation: undefined,
                distanceDelineations: [],
            } as ProximityDetails;
            details.proximityLocations.sort((pl1: ProximityLocation, pl2: ProximityLocation) =>
                pl1.distance > pl2.distance ? 1 : -1
            );
            let first = true;
            delineations.forEach((i) => {
                proximityDetails.distanceDelineations.push({
                    numberWithinMiles: 0,
                    scheduleWithinMiles: 0,
                    tivWithinMiles: 0,
                    scheduleTivWithinMiles: 0,
                    distance: i,
                    withinMilesLocations: [],
                    locationsAggCheckFailed: {},
                });
            });
            details.proximityLocations.forEach((pl: ProximityLocation) => {
                if (first) {
                    proximityDetails.nearestLocation = pl;
                    first = false;
                }
                for (const dd of proximityDetails.distanceDelineations) {
                    if (pl.distance < dd.distance) {
                        dd.numberWithinMiles += 1;
                        dd.tivWithinMiles += pl.totalInsurableValue;
                        dd.withinMilesLocations.push(pl);
                    }
                }
            });
            setLocationProximityDetails(proximityDetails);
        }
    };

    useEffect(() => {
        fetchLocationDetails();
    }, [locationData]);

    const handleNearestClick = (nearestLocation?: ProximityLocation) => {
        // do nothing;
    };

    return (
        <>
            <Dialog
                open={open}
                fullWidth={true}
                onClose={() => onClose && onClose()}
                maxWidth={'lg'}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                scroll="paper"
                disableEnforceFocus
            >
                <DialogTitle id="title" onClose={() => onClose && onClose()}>
                    Location Details
                    {locationDetails.primaryLimit ? (
                        <span style={{ color: theme.palette.error.main }}>{` (${locationDetails.primaryLimit})`}</span>
                    ) : (
                        <></>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Grid container style={{ height: '70vh' }}>
                        <Grid xs={2}>
                            <Grid container>
                                {'referenceId' in locationData && !!locationData.referenceId ? (
                                    <Grid xs={12}>
                                        <Link
                                            href={'/submission/' + locationData.referenceId + '/sov'}
                                            underline="hover"
                                        >
                                            <Typography variant="h3">{locationData.locationName}</Typography>
                                        </Link>
                                    </Grid>
                                ) : (
                                    <Grid xs={12}>
                                        <Typography variant="h3">{locationData.locationName}</Typography>
                                    </Grid>
                                )}
                                {'accountName' in locationData && !!locationData.accountName && (
                                    <Grid xs={12}>
                                        <Typography variant="h4">{locationData.accountName}</Typography>
                                    </Grid>
                                )}
                                <Grid xs={12}>
                                    <Typography variant="h4">{locationData.formattedAddress}</Typography>
                                </Grid>
                                {!!locationDetails.totalInsurableValue && (
                                    <LocationValuesComponent locationData={locationDetails} />
                                )}
                                {!!locationDetails.aopDeductible && (
                                    <Grid xs={12}>
                                        <Typography variant="h3">Deductibles</Typography>
                                        <LabeledData title="AOP" value={locationDetails.aopDeductible || 'N/A'} />
                                        <LabeledData title="Water Dmg." value={locationDetails.wdDeductible || 'N/A'} />
                                        <LabeledData title="Wind/Hail" value={locationDetails.whhDeductible || 'N/A'} />
                                        <LabeledData
                                            title="Named Storm"
                                            value={locationDetails.nsDeductible || 'N/A'}
                                        />
                                        <LabeledData title="Earthquake" value={locationDetails.eqDeductible || 'N/A'} />
                                        <LabeledData title="Flood" value={locationDetails.floodDeductible || 'N/A'} />
                                    </Grid>
                                )}
                                {!!locationProximityDetails && (
                                    <Grid xs={12}>
                                        <Typography variant="h3">
                                            Proximity
                                            <Typography variant={'h5'} component={'span'}>
                                                <i> w/SOV</i>
                                            </Typography>
                                        </Typography>
                                        <LabeledData
                                            title="Nearest Location"
                                            value={`${
                                                locationProximityDetails.nearestLocation?.name
                                            } is ${locationProximityDetails.nearestLocation?.distance.toFixed(
                                                2
                                            )} miles away`}
                                            // onClick={() => handleNearestClick(locationProximityDetails.nearestLocation)}
                                        />
                                        {locationProximityDetails.distanceDelineations.map((dd) => (
                                            // (dd.numberWithinMiles > 0) ? (<LabeledData key={dd.distance} title={`Locs w/in ${dd.distance} Miles:`} value={`${dd.numberWithinMiles} at ${formatTruncatedDollar(dd.tivWithinMiles, true)} TIV`} />) : (<></>)
                                            <div
                                                className={dd.numberWithinMiles > 0 ? classes.proximityLocations : ''}
                                                onClick={(event) => {
                                                    if (dd.numberWithinMiles > 0) {
                                                        setLocationAggPopperAnchorEl(event.currentTarget);
                                                        setLocationAggProxLocations(
                                                            dd.withinMilesLocations.map((l) => {
                                                                return {
                                                                    ...l,
                                                                    hasFailedAggCheck:
                                                                        l.id in (dd.locationsAggCheckFailed || {}),
                                                                    aggCheckOverage: (dd.locationsAggCheckFailed || {})[
                                                                        l.id
                                                                    ],
                                                                };
                                                            })
                                                        );
                                                        setLocationAggDistance(dd.distance);
                                                    }
                                                }}
                                            >
                                                <LabeledData
                                                    key={dd.distance}
                                                    title={`Locs w/in ${dd.distance} Miles:`}
                                                    value={
                                                        <>
                                                            {`${dd.numberWithinMiles} ${
                                                                dd.numberWithinMiles > 0
                                                                    ? ' at ' +
                                                                      formatTruncatedDollar(dd.tivWithinMiles, true) +
                                                                      ' TIV'
                                                                    : ''
                                                            }`}
                                                            <Typography variant={'subtitle1'}>
                                                                <i>
                                                                    {`${
                                                                        dd.scheduleWithinMiles > 0
                                                                            ? dd.numberWithinMiles +
                                                                              dd.scheduleWithinMiles
                                                                            : ''
                                                                    } ${
                                                                        dd.scheduleWithinMiles > 0
                                                                            ? ' at ' +
                                                                              formatTruncatedDollar(
                                                                                  dd.tivWithinMiles +
                                                                                      dd.scheduleTivWithinMiles,
                                                                                  true
                                                                              ) +
                                                                              ' TIV'
                                                                            : ''
                                                                    }`}
                                                                </i>
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid xs={10}>
                            <LrStreetView height="100%" width="100%" location={locationData} allowFullScreen={false} />
                        </Grid>
                    </Grid>
                    {!!locationAggPopperAnchorEl && (
                        <ClickAwayListener
                            onClickAway={(e: any) => {
                                // if(!e.target.textContent.startsWith('Locs')) {
                                setLocationAggPopperAnchorEl(null);
                                // }
                            }}
                        >
                            <Popper
                                open={!!locationAggPopperAnchorEl}
                                anchorEl={locationAggPopperAnchorEl}
                                style={{ zIndex: 2000 }}
                            >
                                <AggregationDetail
                                    proximityLocations={locationAggProxLocations}
                                    distance={locationAggDistance}
                                    referenceTIV={locationDetails.totalInsurableValue}
                                />
                            </Popper>
                        </ClickAwayListener>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default withTheme<DefaultTheme, typeof LocationDetailDialog>(LocationDetailDialog);
